import { EventBuffer } from "../core/buffer"
import {
  captureChange,
  captureClick,
  captureConsole,
  captureError,
  captureInput,
  capturePageView,
  captureSubmit,
  currentPath,
  setRoutePatterns,
  COMMON_USER_NAME,
  COMMON_USER_EMAIL,
  COMMON_USER_ID,
  COMMON_PHONE,
  COMMON_ADDRESS,
  EMAIL_REGEX,
  sanitizeText,
} from "./collectors"
import { observeAssetErrors, observeDomMutations, observeThreadFreezes } from "./diagnostics"
import { detectDevice, generateTraceparent, getVisitorId, uuid } from "../core/identity"
import { startReplay, type ReplayRecorder } from "./replay"
import { Transport, type TransportTargets } from "../core/transport"
import { initVitalsObserver } from "./vitals"
import type { ReliaClient, ReliaOptions, SessionEvent, SessionMeta } from "../core/types"

const FLUSH_INTERVAL_MS = 4000

/** True if a URL is RELIA's own telemetry / dashboard API — never record it as a user network event. */
export function isReliaTelemetryUrl(urlStr: string): boolean {
  const u = (urlStr || "").toLowerCase()
  if (!u) return false
  // SDK write paths
  if (u.includes("/sessions/ingest") || u.includes("/sessions/replay") || u.includes("/sessions/finalize")) return true
  // Dashboard read paths (list/counts/projects/summary/events/replay/flows).
  // These run on the same origin when RELIA monitors itself — recording them
  // creates self-referential network_error rows (e.g. an aborted replay fetch
  // shows up as Error(1) on the list page but is filtered from details).
  if (u.includes("/api/v1/sessions")) return true
  return false
}

/** True for intentional request cancellation — never an app error. */
export function isAbortError(err: unknown): boolean {
  if (!err) return false
  if (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError") return true
  const name = (err as { name?: unknown })?.name
  if (name === "AbortError" || name === "CanceledError") return true
  const msg = String((err as { message?: unknown })?.message ?? err).toLowerCase()
  return msg.includes("aborted") || msg.includes("signal is aborted") || msg.includes("canceled") || msg.includes("cancelled")
}

export let globalClient: ReliaClient | null = null

export function init(options: ReliaOptions): ReliaClient {
  if (typeof window === "undefined") {
    return {
      stop: () => undefined,
      identify: () => undefined,
      track: () => undefined,
    }
  }

  const endpoint = options.endpoint.replace(/\/+$/, "")
  const captureText = options.captureText ?? true
  const withReplay = options.replay ?? true

  setRoutePatterns(options.routePatterns)

  const visitorId = getVisitorId()
  const sessionId = uuid()
  function sanitizeUserTraits(traits: Record<string, any> = {}): Record<string, any> {
    const result: Record<string, any> = {}
    for (const [k, v] of Object.entries(traits)) {
      const lk = k.toLowerCase()
      // Strictly preserve IP addresses for geo-location lookups
      if (lk === "ip" || lk === "client_ip" || lk === "client.ip" || lk === "remote_addr" || lk === "client_address") {
        result[k] = v
      } else if (lk.includes("email") || lk === "mail") {
        result[k] = COMMON_USER_EMAIL
      } else if (lk.includes("name") || lk === "username" || lk === "user") {
        result[k] = COMMON_USER_NAME
      } else if (lk.includes("phone") || lk.includes("mobile") || lk === "tel" || lk === "cell") {
        result[k] = COMMON_PHONE
      } else if (lk.includes("address") || lk.includes("street") || lk === "zip" || lk === "zipcode" || lk.includes("postal") || lk === "city" || lk === "state") {
        result[k] = COMMON_ADDRESS
      } else if (lk.includes("password") || lk.includes("token") || lk.includes("secret") || lk.includes("auth") || lk.includes("ssn") || lk.includes("card") || lk === "dob") {
        result[k] = "[REDACTED]"
      } else if (typeof v === "string") {
        result[k] = sanitizeText(v)
      } else {
        result[k] = v
      }
    }
    return result
  }

  const startedAt = new Date()
  const hasUser = Boolean(options.user_id || options.user_email)
  const meta: SessionMeta = {
    session_id: sessionId,
    visitor_id: visitorId,
    project_id: options.project_id,
    project_name: options.project_name,
    service_name: options.service_name,
    started_at: startedAt.toISOString(),
    user_agent: window.navigator.userAgent,
    device: detectDevice(window.navigator.userAgent),
    screen_size: `${window.screen.width}x${window.screen.height}`,
    user_id: hasUser ? COMMON_USER_ID : "",
    user_email: options.user_email ? COMMON_USER_EMAIL : "",
    user_traits: options.user_traits ? sanitizeUserTraits(options.user_traits) : {},
  }

  const targets: TransportTargets = {
    ingestUrl: `${endpoint}/sessions/ingest`,
    finalizeUrl: `${endpoint}/sessions/finalize`,
    replayUrl: `${endpoint}/sessions/replay`,
  }
  const transport = new Transport(targets, meta)

  const buffer = new EventBuffer(500)
  let eventCount = 0
  let errorCount = 0
  let stopped = false
  let uploading = false

  // Track when the current page was entered for accurate dwell-time calculation
  let pageEnteredAt = Date.now()
  let pageEnteredPath = currentPath()

  // Debounce input events — capture after 800ms pause in typing
  const INPUT_DEBOUNCE_MS = 800
  const inputTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>()
  const inputLastValue = new WeakMap<Element, string>()

  const originalConsoleError = console.error.bind(console)
  const originalConsoleWarn = console.warn.bind(console)
  const originalConsoleLog = console.log.bind(console)

  function push(event: SessionEvent, isError = false): void {
    if (stopped) return
    buffer.push(event)
    eventCount += 1
    if (isError) errorCount += 1
  }

  async function flush(): Promise<void> {
    if (stopped || uploading) return
    const events = buffer.take()
    if (!events.length) return
    uploading = true
    try {
      await transport.sendJourney(events)
    } catch {
      for (const e of events) buffer.push(e)
    } finally {
      uploading = false
    }
  }

  function onClick(e: MouseEvent): void {
    if (e.target instanceof Element) {
      push(captureClick(e.target, captureText))
    }
  }

  function onInput(e: Event): void {
    const t = e.target
    if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return
    // Debounce: cancel previous timer, start new one
    const prev = inputTimers.get(t)
    if (prev) clearTimeout(prev)
    inputTimers.set(
      t,
      setTimeout(() => {
        inputTimers.delete(t)
        const val = t.value
        // Skip if value hasn't changed since last capture
        if (inputLastValue.get(t) === val) return
        inputLastValue.set(t, val)
        push(captureInput(t, captureText))
      }, INPUT_DEBOUNCE_MS),
    )
  }

  // Flush debounced input immediately when the user leaves the field
  function onInputBlur(e: FocusEvent): void {
    const t = e.target
    if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return
    const timer = inputTimers.get(t)
    if (timer) {
      clearTimeout(timer)
      inputTimers.delete(t)
    }
    const val = t.value
    // Skip if value hasn't changed since last capture
    if (inputLastValue.get(t) === val) return
    inputLastValue.set(t, val)
    push(captureInput(t, captureText))
  }

  function onChange(e: Event): void {
    const t = e.target
    if (t instanceof HTMLSelectElement || t instanceof HTMLInputElement) {
      push(captureChange(t))
    }
  }

  function onSubmit(e: SubmitEvent | Event): void {
    const t = e.target
    if (t instanceof HTMLFormElement) {
      push(captureSubmit(t))
    }
  }

  function isIgnoredErrorSource(filename: string, stack?: string): boolean {
    if (
      filename.startsWith("chrome-extension://") ||
      filename.startsWith("moz-extension://") ||
      filename.startsWith("safari-web-extension://") ||
      filename.startsWith("edge-extension://") ||
      filename.includes("node_modules/") ||
      filename.includes("node_modules\\")
    ) {
      return true
    }
    if (stack) {
      if (
        stack.includes("chrome-extension://") ||
        stack.includes("moz-extension://") ||
        stack.includes("safari-web-extension://") ||
        stack.includes("edge-extension://")
      ) {
        return true
      }
      const lines = stack.split("\n")
      for (const line of lines) {
        if (line.includes("node_modules/") || line.includes("node_modules\\")) {
          const frame = line.trim()
          if (frame.startsWith("at ") || frame.startsWith("@")) {
            return true
          }
        }
      }
    }
    return false
  }

  function onWindowError(e: ErrorEvent): void {
    const fn = e.filename || ""
    const stack = e.error?.stack || ""
    if (isIgnoredErrorSource(fn, stack)) {
      return
    }
    push(
      captureError(e.message, e.error, {
        filename: e.filename || "",
        lineno: e.lineno || 0,
        colno: e.colno || 0,
      }),
      true,
    )
  }

  function onRejection(e: PromiseRejectionEvent): void {
    const reason = e.reason
    const stack = reason instanceof Error ? (reason.stack || "") : ""
    if (isIgnoredErrorSource("", stack)) {
      return
    }
    push(
      captureError(reason instanceof Error ? reason.message : String(reason), reason),
      true,
    )
  }

  function onConsole(level: "error" | "warn") {
    return (...args: unknown[]): void => {
      if (level === "error") originalConsoleError(...args)
      else originalConsoleWarn(...args)
      const text = args.map(String).join(" ")
      if (isIgnoredErrorSource("", text)) {
        return
      }
      push(captureConsole(level, text), level === "error")
    }
  }

  let recorder: ReplayRecorder | null = null

  let finalized = false
  function finalize(): void {
    if (finalized) return
    finalized = true
    recorder?.flush()
    void flush()
    transport.beaconFinalize({
      session_id: sessionId,
      visitor_id: visitorId,
      project_id: options.project_id,
      started_at: meta.started_at,
      ended_at: new Date().toISOString(),
      duration_ms: Date.now() - startedAt.getTime(),
      event_count: eventCount,
      error_count: errorCount,
      has_replay: withReplay,
      user_agent: meta.user_agent,
      device: meta.device,
      screen_size: meta.screen_size,
    })
  }

  function onPageHide(): void {
    finalize()
  }

  function onVisibility(): void {
    if (document.visibilityState === "hidden") finalize()
  }

  function onRouteChanged(): void {
    const routeChangeStart = typeof performance !== "undefined" ? performance.now() : Date.now()
    // Emit a page_leave for the page we are leaving, recording accurate dwell time
    const dwellMs = Date.now() - pageEnteredAt
    const leavingPath = pageEnteredPath
    push({
      type: "page_leave",
      ts: new Date().toISOString(),
      target: "",
      path: leavingPath,
      text: "",
      meta: { dwell_ms: dwellMs },
    })

    // Delay slightly to let document.title and new route render
    setTimeout(() => {
      const renderDuration = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) - routeChangeStart
      )
      pageEnteredAt = Date.now()
      pageEnteredPath = currentPath()
      push(capturePageView())

      if (renderDuration > 500) {
        push({
          type: "route_transition",
          ts: new Date().toISOString(),
          target: pageEnteredPath,
          path: pageEnteredPath,
          text: `Route rendered in ${renderDuration}ms`,
          meta: {
            duration_ms: renderDuration,
            from_path: leavingPath,
            to_path: pageEnteredPath,
          },
        })
      }
      void flush()
    }, 50)
  }

  // Intercept SPA History pushState and replaceState (React Router, Next.js, Vue Router)
  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = function (...args: Parameters<typeof originalPushState>): void {
    originalPushState(...args)
    onRouteChanged()
  }

  window.history.replaceState = function (...args: Parameters<typeof originalReplaceState>): void {
    originalReplaceState(...args)
    onRouteChanged()
  }

  const stopConsoleError = onConsole("error")
  const stopConsoleWarn = onConsole("warn")
  console.error = stopConsoleError
  console.warn = stopConsoleWarn

  // Core Web Vitals PerformanceObserver (LCP, INP, CLS, TTFB, FCP)
  let stopVitals: (() => void) | undefined
  if (options.captureVitals !== false) {
    stopVitals = initVitalsObserver((ev) => push(ev))
  }

  // Enterprise UX Diagnostics (Thread freezes, asset load errors, DOM mutations)
  const stopFreezes = observeThreadFreezes((ev) => push(ev))
  const stopAssetErrors = observeAssetErrors((ev) => push(ev, true))
  const stopDomMutations = observeDomMutations((ev) => push(ev))

  // Replay recorder pushes rrweb chunks straight to the backend
  let replayChunkIndex = 0
  if (withReplay) {
    recorder = startReplay(
      async (events) => {
        if (stopped) return
        try {
          await transport.sendReplay(events, replayChunkIndex++)
        } catch {
          // dropped on failure
        }
      },
      2500,
      options,
    )
  }

  // Network / Fetch interceptor with safe traceparent propagation
  const originalFetch = window.fetch.bind(window)
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const urlStr = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request)?.url || ""
    const isRelia = isReliaTelemetryUrl(urlStr)

    if (isRelia || stopped) {
      return originalFetch(input, init)
    }

    const { header, traceId } = generateTraceparent()

    let isSameOriginOrBackend = false
    if (urlStr.startsWith("/") && !urlStr.startsWith("//")) {
      isSameOriginOrBackend = true
    } else {
      try {
        const parsed = new URL(urlStr, window.location.href)
        if (parsed.origin === window.location.origin || parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
          isSameOriginOrBackend = true
        } else if (endpoint && urlStr.includes(new URL(endpoint, window.location.href).hostname)) {
          isSameOriginOrBackend = true
        }
      } catch {
        isSameOriginOrBackend = false
      }
    }

    let fetchInput = input
    let fetchInit = init

    // Only inject custom header into same-origin/backend endpoints so 3rd party auth (Clerk, Stripe) is NEVER blocked by CORS
    if (isSameOriginOrBackend) {
      const reqHeaders = new Headers(init?.headers || (input instanceof Request ? input.headers : {}))
      if (!reqHeaders.has("traceparent")) {
        reqHeaders.set("traceparent", header)
      }
      fetchInit = { ...init, headers: reqHeaders }
      if (input instanceof Request) {
        fetchInput = new Request(input, { headers: reqHeaders })
      }
    }

    const method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase()
    const start = typeof performance !== "undefined" ? performance.now() : Date.now()

    try {
      const response = await originalFetch(fetchInput, fetchInit)
      const duration = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - start)
      const isError = response.status >= 400

      push({
        type: "network",
        ts: new Date().toISOString(),
        target: "",
        path: currentPath(),
        text: `${method} ${urlStr.split("?")[0]} - ${response.status}`,
        meta: {
          url: urlStr,
          method,
          status: String(response.status),
          duration_ms: String(duration),
          trace_id: isSameOriginOrBackend ? traceId : "",
        },
      }, isError)

      return response
    } catch (err: any) {
      const duration = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - start)
      // Aborted requests (navigation away, React StrictMode cleanup, paginated
      // replay streaming) are intentional — never count them as errors.
      if (isAbortError(err)) throw err
      push({
        type: "network_error",
        ts: new Date().toISOString(),
        target: "",
        path: currentPath(),
        text: `${method} ${urlStr.split("?")[0]} FAILED`,
        meta: {
          url: urlStr,
          method,
          error: err?.message || String(err),
          duration_ms: String(duration),
          trace_id: isSameOriginOrBackend ? traceId : "",
        },
      }, true)
      throw err
    }
  }

  // XMLHttpRequest interceptor
  const XHROpen = XMLHttpRequest.prototype.open
  const XHRSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
    const xhr = this as XMLHttpRequest & { _relia_method?: string; _relia_url?: string; _relia_start?: number }
    xhr._relia_method = method.toUpperCase()
    xhr._relia_url = typeof url === "string" ? url : url.toString()
    xhr._relia_start = typeof performance !== "undefined" ? performance.now() : Date.now()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (XHROpen as any).call(this, method, url)
  }
  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const xhr = this as XMLHttpRequest & { _relia_method?: string; _relia_url?: string; _relia_start?: number }
    this.addEventListener("loadend", function () {
      if (stopped) return
      const urlStr = xhr._relia_url || ""
      if (isReliaTelemetryUrl(urlStr)) return
      // status 0 = aborted / blocked request, not an app error — skip it.
      if (this.status === 0) return
      const method = xhr._relia_method || "GET"
      const duration = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - (xhr._relia_start || 0))
      const isError = this.status >= 400
      push({
        type: isError ? "network_error" : "network",
        ts: new Date().toISOString(),
        target: "",
        path: currentPath(),
        text: `${method} ${urlStr.split("?")[0]} - ${this.status}`,
        meta: {
          url: urlStr,
          method,
          status: String(this.status),
          duration_ms: String(duration),
          trace_id: "",
        },
      }, isError)
    })
    return XHRSend.call(this, body)
  }

  // Initial events
  push({ type: "session_start", ts: meta.started_at, target: "", path: currentPath(), text: "", meta: {} })
  push(capturePageView())

  // Flush initial session_start immediately so the session appears instantly in dashboard
  void flush()

  const timer = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)

  function identify(_userId: string, traits: Record<string, any> = {}): void {
    const sanitized = sanitizeUserTraits(traits)
    meta.user_id = COMMON_USER_ID
    meta.user_email = COMMON_USER_EMAIL
    meta.user_traits = {
      ...(meta.user_traits || {}),
      ...sanitized,
      name: COMMON_USER_NAME,
      email: COMMON_USER_EMAIL,
    }
    transport.updateMeta(meta)
    push({
      type: "custom",
      ts: new Date().toISOString(),
      target: "identify",
      path: currentPath(),
      text: `Identified: ${COMMON_USER_NAME} <${COMMON_USER_EMAIL}>`,
      meta: {
        user_id: COMMON_USER_ID,
        user_email: COMMON_USER_EMAIL,
        user_name: COMMON_USER_NAME,
        ...sanitized,
      },
    })
    void flush()
  }

  function track(eventName: string, properties: Record<string, any> = {}): void {
    push({
      type: "custom",
      ts: new Date().toISOString(),
      target: eventName,
      path: currentPath(),
      text: eventName,
      meta: { ...(properties as any) },
    })
    void flush()
  }

  function stop(): void {
    if (stopped) return
    stopped = true
    clearInterval(timer)
    stopVitals?.()
    stopFreezes?.()
    stopAssetErrors?.()
    stopDomMutations?.()
    recorder?.stop()
    window.fetch = originalFetch
    XMLHttpRequest.prototype.open = XHROpen
    XMLHttpRequest.prototype.send = XHRSend
    window.history.pushState = originalPushState
    window.history.replaceState = originalReplaceState
    document.removeEventListener("click", onClick, true)
    document.removeEventListener("input", onInput, true)
    document.removeEventListener("blur", onInputBlur, true)
    document.removeEventListener("change", onChange, true)
    document.removeEventListener("submit", onSubmit, true)
    window.removeEventListener("error", onWindowError)
    window.removeEventListener("unhandledrejection", onRejection)
    window.removeEventListener("pagehide", onPageHide)
    window.removeEventListener("popstate", onRouteChanged)
    window.removeEventListener("hashchange", onRouteChanged)
    document.removeEventListener("visibilitychange", onVisibility)
    console.error = originalConsoleError
    console.warn = originalConsoleWarn
    finalize()
  }

  document.addEventListener("click", onClick, true)
  document.addEventListener("input", onInput, true)
  document.addEventListener("blur", onInputBlur, true)
  document.addEventListener("change", onChange, true)
  document.addEventListener("submit", onSubmit, true)
  window.addEventListener("error", onWindowError)
  window.addEventListener("unhandledrejection", onRejection)
  window.addEventListener("pagehide", onPageHide)
  window.addEventListener("popstate", onRouteChanged)
  window.addEventListener("hashchange", onRouteChanged)
  document.addEventListener("visibilitychange", onVisibility)

  const client: ReliaClient = { stop, identify, track }
  globalClient = client
  return client
}

export function identify(userId: string, traits?: Record<string, any>): void {
  globalClient?.identify(userId, traits)
}

export function track(eventName: string, properties?: Record<string, any>): void {
  globalClient?.track(eventName, properties)
}
